// Browser-safe.

export const NOTIFY_CATEGORIES = [
  'recommendations',
  'fallback',
  'fills',
  'autopilot',
  'markets',
  'portfolio',
  'account',
  'desk',
] as const;

export type NotifyCategory = (typeof NOTIFY_CATEGORIES)[number];

export interface CategoryMeta {
  label: string;
  blurb: string;
}

export const CATEGORY_META: Record<NotifyCategory, CategoryMeta> = {
  recommendations: {
    label: 'Recommendations',
    blurb:
      'New recommendations, revised levels, expiries, rolls and closes, a key event ahead, and your own stop trailed or level reached',
  },
  fallback: {
    label: 'Stops and rolls',
    blurb:
      'Your fallback stop parked, pulled, fired, fired with no order, gone or missing, and your contract expiring, rolled or not rolled',
  },
  fills: {
    label: 'Fills',
    blurb:
      'What was bought, sold and cancelled at your broker, orders it would not take, and square-offs that did not go',
  },
  autopilot: {
    label: 'Autopilot',
    blurb: 'What autopilot took, skipped and squared off for you',
  },
  markets: {
    label: 'Markets',
    blurb:
      'News as it happens, the morning digest before the open, where FIIs, DIIs, clients and pros stand each evening, and NSE circulars that change your stocks’ contracts; to the admins, changes to the trading calendar',
  },
  portfolio: {
    label: 'Your investments',
    blurb: 'What your funds bought and sold each month, as their houses disclosed it',
  },
  account: {
    label: 'Your account',
    blurb:
      'Your access, follows, analyst application and invites decided, invites earned, your broker ready to log in, and a Telegram or P&L link that stopped working',
  },
  desk: {
    label: 'The desk',
    blurb:
      'Requests to decide, invites used and who came in on one, drafts, channel posts, feedback and the desk’s own broker session',
  },
};

/** Any voice but `ledger` starts a Telegram message; the inbox keeps every note regardless. */
export const NOTIFY_VOICES = ['alarm', 'ask', 'tell', 'ledger'] as const;

export type NotifyVoice = (typeof NOTIFY_VOICES)[number];

export interface KindMeta {
  label: string;
  category: NotifyCategory;
  voice: NotifyVoice;
  emoji?: string;
}

/** Order within a category is chip order. */
const KINDS = {
  'new-recommendation': {
    label: 'New recommendation',
    category: 'recommendations',
    voice: 'ask',
    emoji: '🆕',
  },
  'recommendation-revised': {
    label: 'Levels revised',
    category: 'recommendations',
    voice: 'ask',
    emoji: '🔧',
  },
  'recommendation-closed': {
    label: 'Recommendation closed',
    category: 'recommendations',
    voice: 'ask',
  },
  // Unanswered, it expires and every desk-placed position on it is squared off.
  'recommendation-roll-due': {
    label: 'Recommendation expiring',
    category: 'recommendations',
    voice: 'alarm',
    emoji: '⏳',
  },
  // Half an hour before a key event that moves it: hold, tighten or close.
  'event-ahead': {
    label: 'Event ahead',
    category: 'recommendations',
    voice: 'ask',
    emoji: '⏰',
  },
  // A member's own stop or target, before the recommendation ends.
  'own-level-reached': {
    label: 'Your level reached',
    category: 'recommendations',
    voice: 'tell',
    emoji: '🎯',
  },
  'stop-trailed': {
    label: 'Stop trailed',
    category: 'recommendations',
    voice: 'ledger',
    emoji: '🔒',
  },
  'recommendation-rolled': {
    label: 'Recommendation rolled',
    category: 'recommendations',
    voice: 'tell',
    emoji: '🔄',
  },

  'fallback-fired': {
    label: 'Fallback stop fired',
    category: 'fallback',
    voice: 'tell',
    emoji: '⚡',
  },
  'fallback-misfired': {
    label: 'Fallback fired, no order',
    category: 'fallback',
    voice: 'alarm',
    emoji: '⚠️',
  },
  'fallback-armed': {
    label: 'Fallback stop parked',
    category: 'fallback',
    voice: 'ledger',
    emoji: '🛡',
  },
  'fallback-unguarded': {
    label: 'No fallback stop',
    category: 'fallback',
    voice: 'alarm',
    emoji: '⚠️',
  },
  'fallback-lapsed': {
    label: 'Fallback stop gone',
    category: 'fallback',
    voice: 'alarm',
    emoji: '🔓',
  },
  'fallback-released': {
    label: 'Fallback stop pulled',
    category: 'fallback',
    voice: 'ledger',
    emoji: '🔓',
  },
  // Unattended, the contract goes to settlement.
  'position-roll-due': {
    label: 'Your contract expiring',
    category: 'fallback',
    voice: 'alarm',
    emoji: '⏳',
  },
  'position-rolled': {
    label: 'Position rolled over',
    category: 'fallback',
    voice: 'ledger',
    emoji: '🔄',
  },
  // Raised with a session or two left, which is why the roll floor is two.
  'roll-failed': { label: 'Roll did not go', category: 'fallback', voice: 'alarm', emoji: '⚠️' },

  'entry-filled': { label: 'Trade taken', category: 'fills', voice: 'ledger', emoji: '✅' },
  'exit-filled': { label: 'Squared off', category: 'fills', voice: 'ledger', emoji: '✅' },
  'entry-cancelled': { label: 'Entry cancelled', category: 'fills', voice: 'tell', emoji: '🔓' },
  'entry-failed': { label: 'Entry refused', category: 'fills', voice: 'alarm', emoji: '⚠️' },
  'exit-failed': { label: 'Exit refused', category: 'fills', voice: 'alarm', emoji: '⚠️' },
  'square-off-failed': {
    label: 'Not squared off',
    category: 'fills',
    voice: 'alarm',
    emoji: '⚠️',
  },

  'autopilot-took': {
    label: 'Autopilot took it',
    category: 'autopilot',
    voice: 'ledger',
    emoji: '🤖',
  },
  'autopilot-exited': {
    label: 'Squared off with the recommendation',
    category: 'autopilot',
    voice: 'ledger',
    emoji: '🤖',
  },
  'autopilot-skipped': {
    label: 'Autopilot left it to you',
    category: 'autopilot',
    voice: 'ask',
    emoji: '🤖',
  },

  'kite-session': { label: 'Desk session needed', category: 'desk', voice: 'alarm', emoji: '🔒' },

  'access-request': { label: 'Access request', category: 'desk', voice: 'ask', emoji: '🔔' },
  'access-decision': { label: 'Access decided', category: 'account', voice: 'tell' },
  'analyst-request': { label: 'Analyst application', category: 'desk', voice: 'ask', emoji: '🔔' },
  'analyst-decision': { label: 'Application decided', category: 'account', voice: 'tell' },
  'follow-request': { label: 'Follow request', category: 'desk', voice: 'ask', emoji: '🔔' },
  'follow-decision': { label: 'Follow decided', category: 'account', voice: 'tell' },
  'follow-invite-used': {
    label: 'Follow invite used',
    category: 'desk',
    voice: 'tell',
    emoji: '🎟️',
  },
  'invite-admitted': {
    label: 'Let in on an invite',
    category: 'desk',
    voice: 'tell',
    emoji: '🎟️',
  },
  feedback: { label: 'Feedback', category: 'desk', voice: 'tell', emoji: '💬' },
  'channel-draft': { label: 'Draft to decide', category: 'desk', voice: 'ask' },
  'channel-post': { label: 'Channel post', category: 'desk', voice: 'ledger' },
  'telegram-unlinked': {
    label: 'Telegram unlinked',
    category: 'account',
    voice: 'tell',
    emoji: '📵',
  },
  'funds-month': {
    label: 'What your funds did',
    category: 'portfolio',
    voice: 'tell',
    emoji: '🧺',
  },
  'pnl-link': { label: 'P&L link broken', category: 'account', voice: 'ask', emoji: '⚠️' },
  'broker-ready': { label: 'Broker ready', category: 'account', voice: 'ask', emoji: '🔑' },
  'invite-decided': {
    label: 'Your invite decided',
    category: 'account',
    voice: 'tell',
    emoji: '🎟️',
  },
  'invite-earned': { label: 'Invite earned', category: 'account', voice: 'tell', emoji: '🎁' },
  // As it lands: aloud to whoever is in what it moves, quietly to everyone else.
  news: { label: 'News', category: 'markets', voice: 'tell', emoji: '📰' },
  'morning-digest': { label: 'Morning digest', category: 'markets', voice: 'tell', emoji: '☀️' },
  'market-calendar': { label: 'Market calendar', category: 'markets', voice: 'tell', emoji: '📅' },
  // A held or followed stock's contracts change: its lot, its strikes, or whether it can roll.
  'nse-circular': { label: 'NSE circular', category: 'markets', voice: 'tell', emoji: '📜' },
  'participant-positions': {
    label: 'FII & DII positions',
    category: 'markets',
    voice: 'tell',
    emoji: '📊',
  },
} as const satisfies Record<string, KindMeta>;

export type NotifyKind = keyof typeof KINDS;

export const KIND_META: Record<NotifyKind, KindMeta> = KINDS;

export const NOTIFY_KINDS = Object.keys(KIND_META) as NotifyKind[];

/** Kinds nothing writes any more; listed so the check constraint keeps their rows, read as a plain Note. */
export const RETIRED_NOTIFY_KINDS = ['entry-waiting', 'entry-cooled', 'wait-lapsed'] as const;

export const isNotifyKind = (raw: string): raw is NotifyKind => raw in KIND_META;

const metaOf = (kind: string): KindMeta | null => (isNotifyKind(kind) ? KIND_META[kind] : null);

export const labelOf = (kind: string): string => metaOf(kind)?.label ?? 'Note';

export type EmojiKind = {
  [K in NotifyKind]: (typeof KINDS)[K] extends { emoji: string } ? K : never;
}[NotifyKind];

export const emojiOf = (kind: EmojiKind): string => KINDS[kind].emoji;

export const noteTitle = (kind: EmojiKind, subject?: string, emoji = emojiOf(kind)): string =>
  `${emoji} ${KIND_META[kind].label}${subject === undefined ? '' : ` — ${subject}`}`;

/** Whose call a note about a recommendation or a position follows, and in which of their books. */
export const bylineOf = (about: { analyst: string; book: string }): string =>
  `${about.analyst} · ${about.book} book`;

export function titleParts(title: string): {
  icon: string | null;
  what: string;
  subject: string | null;
} {
  const icon = /^[^\w#(]+(?=\s)/u.exec(title)?.[0] ?? null;
  const bare = icon === null ? title : title.slice(icon.length + 1);
  const cut = bare.indexOf(' — ');
  return cut === -1
    ? { icon, what: bare, subject: null }
    : { icon, what: bare.slice(0, cut), subject: bare.slice(cut + 3) };
}

export function bareTitle(title: string): string {
  const { what, subject } = titleParts(title);
  return subject === null ? what : `${what} — ${subject}`;
}

export function voiceOf(note: {
  kind: NotifyKind;
  voice?: NotifyVoice;
  copy: { more: readonly object[] };
}): NotifyVoice {
  if (note.copy.more.some((block) => 'warn' in block)) return 'alarm';
  return note.voice ?? KIND_META[note.kind].voice;
}

export const tradeVoice = (byAutopilot: boolean): NotifyVoice => (byAutopilot ? 'ledger' : 'tell');

export const categoryOf = (kind: string): NotifyCategory | null => metaOf(kind)?.category ?? null;

export const kindsIn = (category: NotifyCategory): NotifyKind[] =>
  NOTIFY_KINDS.filter((kind) => KIND_META[kind].category === category);

export const isNotifyCategory = (raw: string): raw is NotifyCategory =>
  (NOTIFY_CATEGORIES as readonly string[]).includes(raw);

/** Whose button a thread of notes shows, newest first: the newest that has one. */
export const actingOf = <T extends { href: string | null }>(rows: readonly T[]): T | undefined =>
  rows.find((row) => row.href !== null);

export const OFFER_KINDS: readonly NotifyKind[] = [
  'new-recommendation',
  'recommendation-revised',
  'autopilot-skipped',
];

/** Kept off Home's notes: the analyst's page keeps their channel. */
export const OFF_HOME: readonly NotifyKind[] = ['channel-post'];

/** A list of only unknown kinds means no filter, not an empty inbox. */
export function parseKindList(raw: string | null): NotifyKind[] | undefined {
  if (raw === null) return undefined;
  const kinds = raw.split(',').filter(isNotifyKind);
  return kinds.length === 0 ? undefined : kinds;
}
